import { html } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { MobxLitElement } from '@adobe/lit-mobx';
import { differenceInDays } from 'date-fns/differenceInDays.js';
import { differenceInHours } from 'date-fns/differenceInHours.js';
import {
  plannedMaintenance,
  viewList,
  dcim,
  sortCheck,
} from './assets/iconset.js';
import { TWStyles } from './tw.js';
import '@digital-realty/ix-button/ix-button.js';
import '@digital-realty/ix-drawer/ix-drawer.js';
import '@digital-realty/ix-icon';
import '@material/web/icon/icon.js';
import { NotificationsState } from './state/NotificationState.js';
import { ApiCallResult, Notification } from './models/notification.js';
import {
  ConfirmationDialogEventDetail,
  FilterDialogEventDetail,
} from './constants/notification-types.js';
import {
  ApiCallState,
  NotificationGroups,
  NotificationStatus,
} from './constants/notifications.js';
import './components/notifications/notification-item.js';
import './components/notifications/grouped-item.js';
import './components/notifications/group-filters.js';
import './components/notifications/date-filters.js';
import './components/notifications/confirmation-dialog.js';
import { NotificationsStyle } from './styles/notifications-style.js';
import './components/notifications/notification-tooltip.js';

export class IxNotifications extends MobxLitElement {
  static styles = [TWStyles, NotificationsStyle];

  @query('group-filters') groupFilters!: HTMLElement;

  @query('ix-icon-button#group-filter-button') groupFiltersButton!: HTMLElement;

  @query('date-filters') dateFilters!: HTMLElement;

  @query('ix-icon-button#date-filters-button') dateFiltersButton!: HTMLElement;

  @state() private showGroupedView = false;

  @state() private showFilters = false;

  @state() private showDateFilters = false;

  @state() private showMarkAllReadConfirmation = false;

  @property({ type: String }) baseApiUrl = '';

  @property({ type: String }) localStorageKey = '';

  async firstUpdated() {
    // Construct ApiClient form baseApiUrl and localStorageKey
    NotificationsState.baseApiUrl = this.baseApiUrl;
    NotificationsState.localStorageKey = this.localStorageKey;
    NotificationsState.ConstructApiClient();
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener(
      'account-switched',
      NotificationsState.NewApiClient
    );
    window.addEventListener(
      'ix-notifications-toggle-drawer',
      this.toggleDrawer
    );
    window.addEventListener('beforeunload', this.handleOnbeforeunload);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.handleOnbeforeunload();
    window.removeEventListener(
      'account-switched',
      NotificationsState.NewApiClient
    );
    window.removeEventListener(
      'ix-notifications-toggle-drawer',
      this.toggleDrawer
    );
    window.removeEventListener('beforeunload', this.handleOnbeforeunload);
  }

  // Handle browser refresh event
  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
  handleOnbeforeunload() {
    NotificationsState.clearStoredNotificationsData();
  }

  toggleDrawer() {
    NotificationsState.showDrawer = !NotificationsState.showDrawer;
    if (!NotificationsState.showDrawer) {
      this.showFilters = false;
      this.showDateFilters = false;
    }
  }

  markAllread() {
    if (NotificationsState.unreadNotificationCount > 0) {
      this.showMarkAllReadConfirmation = true;
    }
  }

  private confirmedMarkAllRead(e: CustomEvent<ConfirmationDialogEventDetail>) {
    // Mark All read if Confirmed True
    if (e.detail.returnValue) {
      const filterNotification = this.applyNotificationFilters();
      const unreadItems = filterNotification?.filter(
        item => item.status === NotificationStatus.UNREAD
      );

      if (unreadItems.length > 0) {
        let allResultSuccesful = true;

        filterNotification?.forEach(item => {
          NotificationsState.setNotificationReadStatus(item.id).then(
            (result: any) => {
              if (allResultSuccesful) allResultSuccesful = result;
            }
          );
        });

        const apiResult = {
          State:
            allResultSuccesful === true
              ? ApiCallState.SUCCESS
              : ApiCallState.ERROR,
          Message:
            allResultSuccesful === true
              ? 'Successfully marked all the notifications as read'
              : 'Error while marking the notifications read, Please try again',
        } as ApiCallResult;

        this.showResultMessage(apiResult, unreadItems[0].id);

        this.requestUpdate();
      }
    }

    this.showMarkAllReadConfirmation = false;
  }

  // eslint-disable-next-line class-methods-use-this
  private showResultMessage(result: ApiCallResult, id: string) {
    window.dispatchEvent(
      new CustomEvent('add-toast', {
        detail: {
          id,
          content: html` <ix-message-toast
            data-testid="notification-read-all-toast"
            toastId=${id}
            .TMessageToast=${result.State.toLowerCase()}
            forceClose
          >
            ${result.Message}
          </ix-message-toast>`,
          autoClose: 3000,
          durationOut: 3000,
          vertical: 'bottom',
          horizontal: 'center',
          animated: true,
          above: false,
        },
      })
    );
  }

  toggleGroupView() {
    this.showGroupedView = !this.showGroupedView;
  }

  displayFilters() {
    this.showFilters = !this.showFilters;
  }

  /* eslint-disable-next-line */
  applyNotificationFilters() {
    const filteredNotifications = NotificationsState.notifications?.filter(
      (notification: Notification) =>
        (notification.resourceType === NotificationGroups.PLANNED_MAINTENANCE &&
          NotificationsState.notificationFilters.SHOW_PLANNED_MAINTENANCE ===
            true) ||
        (notification.resourceType === NotificationGroups.SERVICE_TICKETS &&
          NotificationsState.notificationFilters.SHOW_SERVICE_TICKETS ===
            true) ||
        (notification.resourceType === NotificationGroups.DCIM_ALERTS &&
          NotificationsState.notificationFilters.SHOW_DCIM_ALERTS === true) ||
        notification.resourceType === NotificationGroups.INCIDENTS
    ); // Always display incidents

    // both dates are defined, search within the window
    if (
      NotificationsState.dateFilters.FROM_DATE !== undefined &&
      NotificationsState.dateFilters.TO_DATE !== undefined
    )
      return filteredNotifications.filter(
        notification =>
          differenceInHours(
            new Date(notification.createdAt),
            NotificationsState.dateFilters.FROM_DATE!
          ) >= 0 &&
          differenceInDays(
            new Date(notification.createdAt),
            NotificationsState.dateFilters.TO_DATE!
          ) <= 0
      );

    // FROM_DATE is defined, TO_DATE is undefined, return notifications after FROM_DATE
    if (
      NotificationsState.dateFilters.FROM_DATE !== undefined &&
      NotificationsState.dateFilters.TO_DATE === undefined
    )
      return filteredNotifications.filter(
        notification =>
          differenceInHours(
            new Date(notification.createdAt),
            NotificationsState.dateFilters.FROM_DATE!
          ) >= 0
      );

    // TO_DATE is defined, FROM_DATE is undefined, return notifications before TO_DATE
    if (
      NotificationsState.dateFilters.FROM_DATE === undefined &&
      NotificationsState.dateFilters.TO_DATE !== undefined
    )
      return filteredNotifications.filter(
        notification =>
          differenceInDays(
            new Date(notification.createdAt),
            NotificationsState.dateFilters.TO_DATE!
          ) <= 0
      );

    return filteredNotifications;
  }

  displayDateFilters() {
    this.showDateFilters = !this.showDateFilters;
  }

  renderGroupedView() {
    const filteredNotifications = this.applyNotificationFilters();
    return html`<div class="-mx-4 -mt-2">
      <grouped-item
        data-testid="planned-maintenance-group"
        groupTitle="Planned Maintenance"
        groupIcon="${plannedMaintenance.strings[0]}"
        .childItems=${filteredNotifications?.filter(
          item => item.resourceType === NotificationGroups.PLANNED_MAINTENANCE
        )}
      ></grouped-item>
      <grouped-item
        data-testid="emergency-repairs-group"
        groupTitle="Emergency Repairs"
        groupIcon="report"
        .childItems=${filteredNotifications?.filter(
          item => item.resourceType === NotificationGroups.INCIDENTS
        )}
      ></grouped-item>
      <grouped-item
        data-testid="service-ticket-updates-group"
        groupTitle="Service Ticket Updates"
        groupIcon="sync_saved_locally"
        .childItems=${filteredNotifications?.filter(
          item => item.resourceType === NotificationGroups.SERVICE_TICKETS
        )}
      ></grouped-item>
      <grouped-item
        data-testid="dcim-alerts-group"
        groupTitle=${NotificationGroups.DCIM_ALERTS}
        groupIcon="${dcim.strings[0]}"
        .childItems=${filteredNotifications?.filter(
          item => item.resourceType === NotificationGroups.DCIM_ALERTS
        )}
      ></grouped-item>
    </div>`;
  }

  renderDefaultView() {
    const filterNotification = this.applyNotificationFilters();

    const filterTodayItems = filterNotification.filter(
      notification =>
        differenceInDays(new Date(notification.createdAt), new Date()) === 0
    );

    const filterEarlierItems = filterNotification.filter(
      notification =>
        differenceInDays(new Date(notification.createdAt), new Date()) !== 0
    );

    if (filterNotification?.length! > 0) {
      return html`
        <h3>Today</h3>
        ${filterTodayItems.length > 0
          ? html`<div class="-mx-4">
              ${filterTodayItems?.map(
                item => html`<notification-item
                  ?read=${item.status !== NotificationStatus.UNREAD}
                  data-testclass="today-notification"
                  .notification=${item}
                  ?disabled=${item.resourceType ===
                  NotificationGroups.INCIDENTS}
                >
                </notification-item> `
              )}
            </div>`
          : this.renderNoResult()}
        <h3>Earlier</h3>
        ${filterEarlierItems.length > 0
          ? html`<div class="-mx-4">
              ${filterEarlierItems?.map(
                item => html`<notification-item
                  ?read=${item.status !== NotificationStatus.UNREAD}
                  data-testclass="earlier-notification"
                  .notification=${item}
                  ?disabled=${item.resourceType ===
                  NotificationGroups.INCIDENTS}
                >
                </notification-item> `
              )}
            </div>`
          : this.renderNoResult()}
      `;
    }

    return html`<h3>Today</h3>
      ${this.renderNoResult()}`;
  }

  /* eslint-disable-next-line */
  renderNoResult() {
    return html`<p class="dlr-text-caption my-4">
      <strong>No notifications yet.</strong>
    </p>`;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleCloseClick(_e: CustomEvent<FilterDialogEventDetail>) {
    this.showDateFilters = false;
    this.showFilters = false;
  }

  // eslint-disable-next-line
  handleContentScroll(e: CustomEvent) {
    // when scrollbar at the end, retrieve next page
    if (
      e !== null &&
      e.detail.scrollTop + e.detail.clientHeight >= e.detail.scrollHeight - 2
    ) {
      NotificationsState.getNotifications();
    }
  }

  manageFilterVisibility(e: Event) {
    const path = e.composedPath();
    if (
      this.showFilters &&
      !path.includes(this.groupFilters) &&
      !path.includes(this.groupFiltersButton)
    ) {
      this.showFilters = false;
    }
    if (
      this.showDateFilters &&
      !path.includes(this.dateFilters) &&
      !path.includes(this.dateFiltersButton)
    ) {
      this.showDateFilters = false;
    }
  }

  render() {
    return html`
      <notification-tooltip data-testid="notifications-tooltip">
      </notification-tooltip>
      <confirmation-dialog
        data-testid="notifications-confirm-read-dialog"
        ?open=${this.showMarkAllReadConfirmation}
        @confirm-dialog-closed=${() => {
          this.showMarkAllReadConfirmation = false;
        }}
        @on-confirm-selection=${this.confirmedMarkAllRead}
        textMessage="Are you sure you want to mark all notifications as read?"
      >
      </confirmation-dialog>
      <ix-drawer
        data-testid="notifications-drawer"
        ?isVisible=${NotificationsState.showDrawer}
        .onClosed=${() => this.toggleDrawer()}
        animate-vertical
        hide-close
        @on-content-scroll=${this.handleContentScroll}
        @click=${this.manageFilterVisibility}
        compact
      >
        <div slot="content" class="m-4">
          <div
            class="flex flex-row justify-between grow items-center notification-header-container mb-4"
          >
            <h2
              data-testid="notifications-heading"
              class="grow items-center dlr-text-heading"
            >
              Notifications
            </h2>
            <div
              class="flex flex-row justify-end grow items-center options"
              style="--md-icon-button-icon-size: 24px;"
            >
              <ix-icon-button
                data-testid="notifications-cta-all-read"
                data-testclass="notifications-cta"
                @click=${this.markAllread}
                @mouseover=${(e: any) => {
                  // only show the tooltip if the button is active
                  if (NotificationsState.unreadNotificationCount > 0) {
                    window.dispatchEvent(
                      new CustomEvent('show-notification-tooltip', {
                        detail: { label: 'Mark All Read', target: e.target },
                      })
                    );
                  }
                }}
                @mouseout=${() =>
                  window.dispatchEvent(
                    new CustomEvent('hide-notification-tooltip')
                  )}
                icon="markunread_mailbox"
                class="${NotificationsState.unreadNotificationCount <= 0
                  ? 'inactive-icon'
                  : 'active-icon'}"
                .disabled="${NotificationsState.unreadNotificationCount <= 0}"
                filledIcon="true"
              ></ix-icon-button>
              <ix-icon-button
                data-testid="notifications-cta-sort-type"
                data-testclass="notifications-cta"
                @click=${this.toggleGroupView}
                @mouseover=${(e: any) => {
                  window.dispatchEvent(
                    new CustomEvent('show-notification-tooltip', {
                      detail: { label: 'Sort by Type', target: e.target },
                    })
                  );
                }}
                @mouseout=${() =>
                  window.dispatchEvent(
                    new CustomEvent('hide-notification-tooltip')
                  )}
                class="active-icon"
                label="Sort by Type"
              >
                <ix-icon slot="default"
                  >${this.showGroupedView ? sortCheck : 'sort'}</ix-icon
                >
              </ix-icon-button>
              <ix-icon-button
                data-testid="notifications-cta-filter-type"
                data-testclass="notifications-cta"
                @click=${this.displayFilters}
                @mouseover=${(e: any) => {
                  window.dispatchEvent(
                    new CustomEvent('show-notification-tooltip', {
                      detail: { label: 'Filter by Type', target: e.target },
                    })
                  );
                }}
                @mouseout=${() =>
                  window.dispatchEvent(
                    new CustomEvent('hide-notification-tooltip')
                  )}
                class="active-icon"
                id="group-filter-button"
                label="Filter by Type"
              >
                <ix-icon slot="default"> ${viewList} </ix-icon>
              </ix-icon-button>
              <div
                data-testid="notifications-filter-dropdown"
                class="filter-dropdown-content  ${this.showFilters
                  ? 'active z-50'
                  : 'hidden'}"
              >
                <group-filters></group-filters>
              </div>
              <ix-icon-button
                data-testid="notifications-cta-filter-date"
                data-testclass="notifications-cta"
                @click=${this.displayDateFilters}
                @mouseover=${(e: any) => {
                  window.dispatchEvent(
                    new CustomEvent('show-notification-tooltip', {
                      detail: { label: 'Filter by Date', target: e.target },
                    })
                  );
                }}
                @mouseout=${() =>
                  window.dispatchEvent(
                    new CustomEvent('hide-notification-tooltip')
                  )}
                icon="date_range"
                class="active-icon"
                filledIcon="true"
                id="date-filters-button"
                label="Filter by Date"
              ></ix-icon-button>
              <div
                data-testid="notifications-date-filter-dropdown"
                class="datefilter-dropdown-content ${this.showDateFilters
                  ? 'active z-50'
                  : 'hidden'}"
                @on-selection=${this.handleCloseClick}
              >
                <date-filters></date-filters>
              </div>
            </div>
          </div>
          ${this.showGroupedView
            ? this.renderGroupedView()
            : this.renderDefaultView()}
        </div>
      </ix-drawer>
    `;
  }
}
